<template>
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      >
        <!-- Overlay -->
        <div
          class="absolute inset-0 bg-black/60"
          @click="close"
        />

        <!-- Modal -->
        <div
          class="relative bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl w-full shadow-2xl
                 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden"
          :class="size === 'large' ? 'max-w-4xl' : 'max-w-3xl'"
        >
          <!-- Header -->
          <div
            class="px-4 sm:px-6 py-4 sm:py-5
                   flex-shrink-0"
            :class="headerClasses"
          >
            <div class="flex items-center gap-3">
              <h3
                class="text-lg sm:text-xl font-bold"
                :class="titleClasses"
              >
                {{ title }}
              </h3>
            </div>
          </div>

          <!-- Body -->
          <div 
            class="px-4 sm:px-6 py-4 sm:py-6 overflow-y-auto flex-1"
            :class="bodyClasses"
          >
            <slot />
          </div>

          <!-- Footer -->
          <div
            class="px-4 sm:px-6 py-3 sm:py-4
                   flex-shrink-0"
            :class="footerClasses"
          >
            <div class="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2 sm:gap-3">
              <slot name="footer">
                <button
                  type="button"
                  class="px-4 sm:px-5 py-2.5 text-sm font-semibold
                         text-slate-700 dark:text-slate-300
                         bg-white dark:bg-slate-700
                         border border-slate-300 dark:border-slate-600
                         rounded-xl hover:bg-slate-50 dark:hover:bg-slate-600
                         transition-all shadow-sm"
                  @click="close"
                >
                  Cancel
                </button>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>


<script lang="ts">
import { defineComponent, computed } from 'vue';

export default defineComponent({
  name: 'Modal',

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'default',
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value: string) => ['default', 'employee'].includes(value),
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const close = () => emit('update:modelValue', false);

    const headerClasses = computed(() => {
      if (props.variant === 'employee') {
        return 'bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10';
      }
      return 'bg-gradient-to-r from-primary/5 to-secondary/5 border-b border-slate-200 dark:border-slate-700';
    });

    const titleClasses = computed(() => {
      if (props.variant === 'employee') {
        return 'text-white';
      }
      return 'text-primary';
    });

    const bodyClasses = computed(() => {
      if (props.variant === 'employee') {
        return 'bg-white dark:bg-gray-800';
      }
      return 'bg-white dark:bg-slate-800';
    });

    const footerClasses = computed(() => {
      if (props.variant === 'employee') {
        return 'bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600';
      }
      return 'bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700';
    });

    return { 
      close,
      headerClasses,
      titleClasses,
      bodyClasses,
      footerClasses,
    };
  },
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
